import { useEffect, useRef, useState } from "react";
import { useWatch, type Control } from "react-hook-form";
import type { MessageTemplateFormValues } from "../formTypes";

type VariableTarget = "header" | "body" | "buttonText" | "buttonValue";

type FieldRef = {
  value: string;
  onChange: (value: string) => void;
};

type RichFormatType = "bold" | "italic" | "strikethrough" | "monospace";

interface UseMessageTemplateFormDialogStateParams {
  control: Control<MessageTemplateFormValues>;
  dialogTitle: string;
  isSubmitting: boolean;
  onClose: () => void;
  open: boolean;
  resetForm: () => void;
  triggerBodyValidation: () => void;
  triggerHeaderValidation: () => void;
}

export function useMessageTemplateFormDialogState({
  control,
  dialogTitle,
  isSubmitting,
  onClose,
  open,
  resetForm,
  triggerBodyValidation,
  triggerHeaderValidation,
}: UseMessageTemplateFormDialogStateParams) {
  const headerInputRef = useRef<HTMLInputElement | null>(null);
  const headerFieldRef = useRef<FieldRef | null>(null);
  const bodyInputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);
  const bodyFieldRef = useRef<FieldRef | null>(null);
  const buttonTextInputRef = useRef<HTMLInputElement | null>(null);
  const buttonValueInputRef = useRef<HTMLInputElement | null>(null);
  const buttonTextFieldRef = useRef<FieldRef | null>(null);
  const buttonValueFieldRef = useRef<FieldRef | null>(null);
  const [activeVariableTarget, setActiveVariableTarget] =
    useState<VariableTarget>("body");
  const [headerInsertLimitError, setHeaderInsertLimitError] = useState<string | null>(
    null,
  );

  const isEditing = dialogTitle.toLowerCase().includes("editar");
  const watchedBodyValue = useWatch({
    control,
    name: "body",
    defaultValue: "",
  });
  const previewHeader = useWatch({
    control,
    name: "header",
    defaultValue: "",
  });
  const previewButtonText = useWatch({
    control,
    name: "buttonText",
    defaultValue: "",
  });
  const hasButton = useWatch({
    control,
    name: "hasButton",
    defaultValue: false,
  });
  const buttonType = useWatch({
    control,
    name: "buttonType",
    defaultValue: "URL",
  });

  useEffect(() => {
    if (!open) return;
    if (!watchedBodyValue?.trim()) return;

    const timer = window.setTimeout(() => {
      triggerBodyValidation();
    }, 3000);

    return () => window.clearTimeout(timer);
  }, [open, triggerBodyValidation, watchedBodyValue]);

  const handleDialogClose = () => {
    if (isSubmitting) return;
    resetForm();
    onClose();
  };

  const insertTokenAtSelection = ({
    currentValue,
    onChange,
    inputElement,
    token,
  }: {
    currentValue: string;
    onChange: (value: string) => void;
    inputElement: HTMLInputElement | HTMLTextAreaElement | null;
    token: string;
  }) => {
    const selectionStart = inputElement?.selectionStart ?? currentValue.length;
    const selectionEnd = inputElement?.selectionEnd ?? currentValue.length;
    const beforeText = currentValue.slice(0, selectionStart);
    const afterText = currentValue.slice(selectionEnd);
    onChange(`${beforeText}${token}${afterText}`);

    const nextCursorPosition = selectionStart + token.length;
    requestAnimationFrame(() => {
      inputElement?.focus();
      inputElement?.setSelectionRange(nextCursorPosition, nextCursorPosition);
    });
  };

  const applyWrapFormatAtSelection = ({
    currentValue,
    onChange,
    inputElement,
    openToken,
    closeToken,
  }: {
    currentValue: string;
    onChange: (value: string) => void;
    inputElement: HTMLInputElement | HTMLTextAreaElement | null;
    openToken: string;
    closeToken: string;
  }) => {
    const selectionStart = inputElement?.selectionStart ?? currentValue.length;
    const selectionEnd = inputElement?.selectionEnd ?? currentValue.length;
    const beforeText = currentValue.slice(0, selectionStart);
    const selectedText = currentValue.slice(selectionStart, selectionEnd);
    const afterText = currentValue.slice(selectionEnd);

    const wrappedText = `${openToken}${selectedText}${closeToken}`;
    onChange(`${beforeText}${wrappedText}${afterText}`);

    requestAnimationFrame(() => {
      inputElement?.focus();
      if (selectionStart === selectionEnd) {
        const nextCursorPosition = selectionStart + openToken.length;
        inputElement?.setSelectionRange(nextCursorPosition, nextCursorPosition);
        return;
      }
      const nextSelectionStart = selectionStart + openToken.length;
      const nextSelectionEnd = nextSelectionStart + selectedText.length;
      inputElement?.setSelectionRange(nextSelectionStart, nextSelectionEnd);
    });
  };

  const getWrapTokensByFormat = (
    formatType: RichFormatType,
  ): { openToken: string; closeToken: string } => {
    if (formatType === "bold") return { openToken: "*", closeToken: "*" };
    if (formatType === "italic") return { openToken: "_", closeToken: "_" };
    if (formatType === "strikethrough") return { openToken: "~", closeToken: "~" };
    return { openToken: "```", closeToken: "```" };
  };

  const handleApplyRichFormat = (
    formatType: RichFormatType,
    target: "header" | "body",
  ) => {
    const { openToken, closeToken } = getWrapTokensByFormat(formatType);

    if (target === "header" && headerFieldRef.current) {
      applyWrapFormatAtSelection({
        currentValue: headerFieldRef.current.value ?? "",
        onChange: headerFieldRef.current.onChange,
        inputElement: headerInputRef.current,
        openToken,
        closeToken,
      });
      requestAnimationFrame(() => {
        setHeaderInsertLimitError(null);
        triggerHeaderValidation();
      });
      return;
    }

    if (bodyFieldRef.current) {
      applyWrapFormatAtSelection({
        currentValue: bodyFieldRef.current.value ?? "",
        onChange: bodyFieldRef.current.onChange,
        inputElement: bodyInputRef.current,
        openToken,
        closeToken,
      });
      requestAnimationFrame(() => {
        triggerBodyValidation();
      });
    }
  };

  const handleInsertVariable = (variableName: string) => {
    const token = `{{${variableName}}}`;

    if (activeVariableTarget === "header" && headerFieldRef.current) {
      const headerValue = headerFieldRef.current.value ?? "";
      const existingHeaderVariables = headerValue.match(/\{\{([^}]+)\}\}/g) ?? [];

      if (existingHeaderVariables.length >= 1) {
        setHeaderInsertLimitError(
          "Não é possível adicionar mais de 1 variável no cabeçalho.",
        );
        triggerHeaderValidation();
        return;
      }

      insertTokenAtSelection({
        currentValue: headerValue,
        onChange: headerFieldRef.current.onChange,
        inputElement: headerInputRef.current,
        token,
      });
      requestAnimationFrame(() => {
        setHeaderInsertLimitError(null);
        triggerHeaderValidation();
      });
      return;
    }

    if (activeVariableTarget === "buttonText" && buttonTextFieldRef.current) {
      insertTokenAtSelection({
        currentValue: buttonTextFieldRef.current.value ?? "",
        onChange: buttonTextFieldRef.current.onChange,
        inputElement: buttonTextInputRef.current,
        token,
      });
      return;
    }

    if (activeVariableTarget === "buttonValue" && buttonValueFieldRef.current) {
      insertTokenAtSelection({
        currentValue: buttonValueFieldRef.current.value ?? "",
        onChange: buttonValueFieldRef.current.onChange,
        inputElement: buttonValueInputRef.current,
        token,
      });
      return;
    }

    if (bodyFieldRef.current) {
      insertTokenAtSelection({
        currentValue: bodyFieldRef.current.value ?? "",
        onChange: bodyFieldRef.current.onChange,
        inputElement: bodyInputRef.current,
        token,
      });
    }
  };

  return {
    activeVariableTarget,
    bodyFieldRef,
    bodyInputRef,
    buttonTextFieldRef,
    buttonTextInputRef,
    buttonType,
    buttonValueFieldRef,
    buttonValueInputRef,
    handleDialogClose,
    handleApplyRichFormat,
    handleInsertVariable,
    hasButton,
    headerFieldRef,
    headerInputRef,
    headerInsertLimitError,
    isEditing,
    previewButtonText,
    previewHeader,
    setActiveVariableTarget,
    setHeaderInsertLimitError,
    watchedBodyValue,
  };
}

